// /app/frontend/src/components/dental_intel/DhiRetiredCards.jsx
// iter1111 — Specific wrapper cards for each retired-score section.
// Each consumes the new backend contract shape (status, factors[], etc.)
// and renders through the FactorCard primitive.
//
// All eight sections live in this file so the DentalHealthIntelligencePage
// can import them from a single location. Keep per-card components tiny
// and composed of the FactorCard primitives.

import React from "react";
import {
  Grid3x3, Beaker, Shield, Microscope, Anchor, CalendarClock,
  LineChart, Share2, ExternalLink,
} from "lucide-react";
import {
  FactorCard, FactorRow, LegacyBadge, RetiredExplainer,
  NotAssessableState, StatusChip,
} from "./FactorCard";

/** Shared helper — compact educational intro for each card.
 *  iter1111.e — patient-oriented. Returns null if we have no useful intro
 *  (so the UI stays clean). Backend `reason_bg/en` is engineering talk
 *  about the audit; we don't surface it to patients. */
function cardIntro(section, lang) {
  // Intentionally return nothing — the FactorCard subtitle already
  // explains the context in patient-friendly terms. See FactorCard.jsx.
  void section; void lang;
  return "";
}

/** Shared helper — source citation summary line.
 *  iter1111.e — removed audit-register pointer; sources appear per row. */
function sourceSummary(section, lang) {
  void section; void lang;
  return "";
}

// ──────────────────────────────────────────────────────────────────
// Card 1 — Oral Ecosystem (5 anatomical zones; factor list, no scores)
// ──────────────────────────────────────────────────────────────────
export function OralEcosystemFactorCard({ data, lang = "bg", isDark = false }) {
  if (!data) return null;
  const factors = data.factors || [];
  
  return (
    <FactorCard
      testid="dhi-factor-card-oral-ecosystem"
      icon={Grid3x3}
      isDark={isDark}
      title={lang === "en" ? "Oral Health Factors by Area"
                           : "Фактори за устно здраве — по области"}
      subtitle={lang === "en"
        ? "Lifestyle factors that may support each oral structure. Clinical status is assessed by your dentist."
        : "Фактори от начина на живот, които подкрепят всяка част от устата. Клиничното състояние се оценява от зъболекаря."}
      source_summary={sourceSummary(data, lang)}
    >
      
      {factors.length === 0 ? (
        <div className={`text-xs ${isDark ? "text-slate-500" : "text-slate-500"}`}>
          {lang === "en" ? "No factors logged yet." : "Все още няма въведени фактори."}
        </div>
      ) : (
        <ul className="space-y-1">
          {factors.map((f, i) => (
            <FactorRow
              key={`${f.zone || "f"}-${i}`}
              testid="dhi-ecosystem-factor-row"
              label_bg={f.factor_bg}
              label_en={f.factor_en}
              note_bg={f.note_bg}
              note_en={f.note_en}
              status={f.status}
              lang={lang}
              isDark={isDark}
            />
          ))}
        </ul>
      )}
    </FactorCard>
  );
}

// ──────────────────────────────────────────────────────────────────
// Card 2 — Oral Metabolic (5 sub-components; no weighted total)
// ──────────────────────────────────────────────────────────────────
export function OralMetabolicFactorCard({ data, lang = "bg", isDark = false }) {
  if (!data) return null;
  const comps = data.components_as_factors || data.components || {};
  
  // Each component in `comps` is a sub-score dict (score+band). Keep only
  // the educational labels; drop the numeric to stay faithful to the audit.
  const keys = ["sugar", "acid", "inflammatory", "insulin", "protective"];
  const labels_bg = {
    sugar: "Захарна експозиция",
    acid: "Киселинна експозиция",
    inflammatory: "Възпалителен фон",
    insulin: "Инсулинова среда (DIL)",
    protective: "Протективни хранителни фактори",
  };
  const labels_en = {
    sugar: "Sugar exposure",
    acid: "Acid exposure",
    inflammatory: "Inflammatory background",
    insulin: "Insulin environment (DIL)",
    protective: "Protective dietary factors",
  };
  const notes_bg = {
    sugar: "Асоциация, не измерване. WHO 2015: силна препоръка под 10 % енергия от захар.",
    acid: "Асоциация. BEWE индексът измерва реалната ерозия клинично.",
    inflammatory: "Асоциация с системно възпаление; индивидуален ефект върху тъканите не се прогнозира.",
    insulin: "Асоциация, не измерване. Няма валидирана индивидуална прогноза за кариес.",
    protective: "Асоциация с устен микробиом и системно възпаление.",
  };
  const notes_en = {
    sugar: "Association, not measurement. WHO 2015: strong recommendation <10 % energy.",
    acid: "Association. BEWE index measures actual erosion clinically.",
    inflammatory: "Associated with systemic inflammation; individual effect on tissues is not predicted.",
    insulin: "Association, not measurement. No validated individual caries prediction.",
    protective: "Association with oral microbiome and systemic inflammation.",
  };
  return (
    <FactorCard
      testid="dhi-factor-card-oral-metabolic"
      icon={Beaker}
      isDark={isDark}
      title={lang === "en" ? "Diet & Metabolic Factors"
                           : "Хранителни и метаболитни фактори"}
      subtitle={lang === "en"
        ? "How your diet and metabolism interact with oral health."
        : "Как храненето и метаболизмът ви взаимодействат с устното здраве."}
      source_summary={sourceSummary(data, lang)}
    >
      
      <ul className="space-y-1">
        {keys.filter((k) => comps[k]).map((k) => (
          <FactorRow
            key={k}
            testid="dhi-metabolic-component-row"
            label_bg={labels_bg[k]}
            label_en={labels_en[k]}
            note_bg={notes_bg[k]}
            note_en={notes_en[k]}
            status="recorded"
            lang={lang}
            isDark={isDark}
          />
        ))}
      </ul>
    </FactorCard>
  );
}

// ──────────────────────────────────────────────────────────────────
// Card 3 — Tooth Protection (behaviour checklist)
// ──────────────────────────────────────────────────────────────────
export function ToothProtectionFactorCard({ data, lang = "bg", isDark = false }) {
  if (!data) return null;
  const items = data.items || [];
  
  return (
    <FactorCard
      testid="dhi-factor-card-tooth-protection"
      icon={Shield}
      isDark={isDark}
      title={lang === "en" ? "Daily Tooth Protection Habits"
                           : "Ежедневни навици за защита на зъбите"}
      subtitle={lang === "en"
        ? "Habits that protect your teeth and gums every day."
        : "Навици, които защитават зъбите и венците всеки ден."}
      source_summary={sourceSummary(data, lang)}
    >
      
      <ul className="space-y-1">
        {items.map((it) => (
          <FactorRow
            key={it.key}
            testid="dhi-protection-item-row"
            label_bg={it.label_bg}
            label_en={it.label_en}
            source_bg={it.source_bg}
            source_en={it.source_en}
            status={it.status}
            lang={lang}
            isDark={isDark}
          />
        ))}
      </ul>
    </FactorCard>
  );
}

// ──────────────────────────────────────────────────────────────────
// Card 4 — Microbiome Balance (educational factor lists)
// ──────────────────────────────────────────────────────────────────
export function MicrobiomeFactorCard({ data, lang = "bg", isDark = false }) {
  if (!data) return null;
  const protective = data.protective || [];
  const cariogenic = data.cariogenic || [];
  const periodontal = data.periodontal_dysbiosis || [];
  
  const groups = [
    { title_bg: "Протективни фактори", title_en: "Protective factors",
      items: protective, status: "present" },
    { title_bg: "Кариогенни фактори", title_en: "Cariogenic factors",
      items: cariogenic, status: "concern" },
    { title_bg: "Фактори за пародонтална дисбиоза",
      title_en: "Periodontal dysbiosis factors",
      items: periodontal, status: "concern" },
  ];
  return (
    <FactorCard
      testid="dhi-factor-card-microbiome"
      icon={Microscope}
      isDark={isDark}
      title={lang === "en" ? "Mouth Microbiome — Lifestyle Factors"
                           : "Устен микробиом — фактори от начина на живот"}
      subtitle={lang === "en"
        ? "Diet choices that help or harm the microbes in your mouth."
        : "Хранителни избори, които помагат или вредят на микробите в устата."}
      source_summary={sourceSummary(data, lang)}
    >
      
      {groups.filter((g) => g.items.length > 0).map((g, gi) => (
        <div key={gi} className="space-y-1">
          <div className={`text-xs font-bold uppercase tracking-wider ${
            isDark ? "text-slate-400" : "text-slate-500"
          }`}>
            {lang === "en" ? g.title_en : g.title_bg}
          </div>
          <ul className="space-y-1">
            {g.items.map((it, i) => (
              <FactorRow
                key={`${gi}-${i}`}
                testid="dhi-microbiome-factor-row"
                label_bg={it}
                label_en={it}
                status={g.status}
                lang={lang}
                isDark={isDark}
              />
            ))}
          </ul>
        </div>
      ))}
    </FactorCard>
  );
}

// ──────────────────────────────────────────────────────────────────
// Card 5 — Implant Readiness (ITI 2018 clinician checklist)
// ──────────────────────────────────────────────────────────────────
export function ImplantReadinessFactorCard({ data, lang = "bg", isDark = false }) {
  if (!data) return null;
  const items = data.items || [];
  
  const modeLabel = lang === "en" ? data.mode_label_en : data.mode_label_bg;
  return (
    <FactorCard
      testid="dhi-factor-card-implant"
      icon={Anchor}
      isDark={isDark}
      title={lang === "en" ? "Implant Readiness — Discuss with Your Dentist"
                           : "Готовност за имплант — обсъди с твоя зъболекар"}
      subtitle={modeLabel ||
        (lang === "en" ? "What to check before and after an implant."
                       : "Какво да проверите преди и след имплант.")}
      source_summary={sourceSummary(data, lang)}
    >
      
      <ul className="space-y-1">
        {items.map((it) => (
          <FactorRow
            key={it.key}
            testid="dhi-implant-item-row"
            label_bg={it.label_bg}
            label_en={it.label_en}
            note_bg={it.note_bg}
            note_en={it.note_en}
            status={it.status}
            lang={lang}
            isDark={isDark}
          />
        ))}
      </ul>
    </FactorCard>
  );
}

// ──────────────────────────────────────────────────────────────────
// Card 6 — Dental Age (chronological + lifestyle factors; NO numeric age)
// ──────────────────────────────────────────────────────────────────
export function DentalAgeFactorCard({ data, lang = "bg", isDark = false }) {
  if (!data) return null;
  const factors = data.factors || [];
  
  const chrono = data.chronological;
  return (
    <FactorCard
      testid="dhi-factor-card-dental-age"
      icon={CalendarClock}
      isDark={isDark}
      title={lang === "en" ? "Your Oral Health Snapshot"
                           : "Твоята картина за устното здраве"}
      subtitle={lang === "en"
        ? "Factors from your lifestyle that interact with oral health."
        : "Фактори от начина ти на живот, които влияят на устното здраве."}
      source_summary={sourceSummary(data, lang)}
    >
      
      {chrono !== null && chrono !== undefined && (
        <div className={`rounded-lg px-3 py-2 text-sm ${
          isDark ? "bg-white/[0.03] text-slate-200" : "bg-slate-50 text-slate-800"
        }`}>
          <span className="font-semibold">
            {lang === "en" ? "Chronological age: " : "Хронологична възраст: "}
          </span>
          <span className="font-mono">{chrono}</span>
        </div>
      )}
      <ul className="space-y-1">
        {factors.filter((f) => f.key !== "chronological_age").map((f) => (
          <FactorRow
            key={f.key}
            testid="dhi-dental-age-factor-row"
            label_bg={f.label_bg}
            label_en={f.label_en}
            note_bg={f.note_bg}
            note_en={f.note_en}
            status={f.status}
            lang={lang}
            isDark={isDark}
          />
        ))}
      </ul>
    </FactorCard>
  );
}

// ──────────────────────────────────────────────────────────────────
// Card 7 — Forecast (educational scenarios; no numeric prediction)
// ──────────────────────────────────────────────────────────────────
export function ForecastFactorCard({ data, lang = "bg", isDark = false }) {
  if (!data) return null;
  const scenarios = data.educational_scenarios || [];
  
  if (scenarios.length === 0) return null;
  return (
    <FactorCard
      testid="dhi-factor-card-forecast"
      icon={LineChart}
      isDark={isDark}
      title={lang === "en" ? "What You Can Try Next"
                           : "Какво можеш да опиташ след това"}
      subtitle={lang === "en"
        ? "Small habit changes that support your oral health."
        : "Малки промени в навиците, които подкрепят устното здраве."}
      source_summary={sourceSummary(data, lang)}
    >
      
      <ul className="space-y-1">
        {scenarios.map((s) => (
          <FactorRow
            key={s.key}
            testid="dhi-forecast-scenario-row"
            label_bg={s.label_bg}
            label_en={s.label_en}
            note_bg={s.rationale_bg}
            note_en={s.rationale_en}
            status="present"
            lang={lang}
            isDark={isDark}
          />
        ))}
      </ul>
    </FactorCard>
  );
}

// ──────────────────────────────────────────────────────────────────
// Card 8 — Systemic Pathways (educational chains + PMIDs)
// ──────────────────────────────────────────────────────────────────
export function SystemicPathwaysFactorCard({ data, lang = "bg", isDark = false }) {
  if (!data) return null;
  const pathways = data.pathways || [];
  
  return (
    <FactorCard
      testid="dhi-factor-card-systemic"
      icon={Share2}
      isDark={isDark}
      title={lang === "en" ? "Mouth & Body — Known Connections"
                           : "Устна кухина и тяло — известни връзки"}
      subtitle={lang === "en"
        ? "How oral health interacts with the rest of your body."
        : "Как устното здраве взаимодейства с останалата част на тялото."}
      source_summary={sourceSummary(data, lang)}
    >
      
      <div className="space-y-3">
        {pathways.map((p) => (
          <div
            key={p.key}
            data-testid="dhi-pathway-row"
            className={`rounded-lg border p-3 ${
              isDark ? "bg-white/[0.02] border-white/5" : "bg-slate-50/80 border-slate-200"
            }`}
          >
            <div className={`text-sm font-bold ${isDark ? "text-slate-100" : "text-slate-900"}`}>
              {lang === "en" ? p.title_en : p.title_bg}
            </div>
            <div className={`mt-1 text-xs leading-relaxed ${
              isDark ? "text-slate-400" : "text-slate-600"
            }`}>
              {lang === "en" ? p.association_en : p.association_bg}
            </div>
            {Array.isArray(p.citations) && p.citations.length > 0 && (
              <div className={`mt-2 flex flex-wrap gap-1 text-[11px] font-mono ${
                isDark ? "text-slate-400" : "text-slate-500"
              }`}>
                {p.citations.map((c, i) => (
                  <span
                    key={i}
                    className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${
                      isDark ? "bg-white/[0.03]" : "bg-white border border-slate-200"
                    }`}
                  >
                    <ExternalLink className="w-3 h-3" />
                    {c}
                  </span>
                ))}
              </div>
            )}
            {p.markers_present && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {Object.entries(p.markers_present).map(([k, v]) => (
                  <StatusChip
                    key={k}
                    status={v?.recorded ? "recorded" : "absent"}
                    isDark={isDark}
                    lang={lang}
                  />
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </FactorCard>
  );
}

// Legacy badge export so historical snapshots render consistently.
export { LegacyBadge, NotAssessableState };
